// Brand Kit view — six tiles per spec §15. Fully wired to the
// /storefront/studio/brand-kit endpoints: logo + headshot upload, agent
// + brokerage info edit, brand colors picker. Photo library tile is a
// placeholder until the saved-photos endpoints land.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Image as ImageIcon,
  User,
  Phone,
  Palette,
  Camera,
  Scale,
  Upload,
  Trash2,
  Plus,
  X,
  Check,
} from 'lucide-react';
import {
  deleteBrandAsset,
  getBrandKit,
  patchBrandKit,
  uploadBrandAsset,
  StudioApiError,
} from '../../lib/api';
import type { BrandKit, BrandKitAsset } from '../../lib/types';
import { qk } from '../../lib/queryClient';
import { useStudio } from '../../lib/store';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PhotoLibrary } from '../../components/PhotoLibrary';
import { listPhotos } from '../../lib/api';
import { qk as queryKeys } from '../../lib/queryClient';
import './brand-kit.css';

export function BrandKitView() {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const kitQ = useQuery({ queryKey: qk.brandKit, queryFn: getBrandKit });
  const kit = kitQ.data?.brand_kit ?? null;

  if (kitQ.isLoading) {
    return (
      <section aria-label="Brand Kit">
        <header className="view-head">
          <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 8 }}>
            Set it once, use it forever
          </p>
          <h1>Brand Kit</h1>
          <p className="sub">Loading your kit…</p>
        </header>
      </section>
    );
  }

  if (!kit) {
    return (
      <section aria-label="Brand Kit">
        <header className="view-head">
          <h1>Brand Kit</h1>
          <p className="sub">Couldn&rsquo;t load your kit. Refresh and try again.</p>
        </header>
      </section>
    );
  }

  return (
    <section aria-label="Brand Kit">
      <header className="view-head">
        <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 8 }}>
          Set it once, use it forever
        </p>
        <h1>Brand Kit</h1>
        <p className="sub">
          Save your logo, headshot, contact info, and colors here. Every new design auto-fills with your branding.
        </p>
      </header>

      <CompletionRibbon kit={kit} />

      <div className="kit-grid">
        <AssetTile
          kind="logo"
          title="Logo"
          icon={<ImageIcon size={28} strokeWidth={1.4} />}
          description="Your brand mark, used on every design."
          asset={kit.logo}
          onUpload={async (f) => {
            const resp = await uploadBrandAsset('logo', f);
            qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
            pushToast({ type: 'success', message: 'Logo updated' });
          }}
          onRemove={async () => {
            const resp = await deleteBrandAsset('logo');
            qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
            pushToast({ type: 'success', message: 'Logo removed' });
          }}
        />
        <AssetTile
          kind="headshot"
          title="Headshot"
          icon={<User size={28} strokeWidth={1.4} />}
          description="Professional photo for cards and signs."
          asset={kit.headshot}
          onUpload={async (f) => {
            const resp = await uploadBrandAsset('headshot', f);
            qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
            pushToast({ type: 'success', message: 'Headshot updated' });
          }}
          onRemove={async () => {
            const resp = await deleteBrandAsset('headshot');
            qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
            pushToast({ type: 'success', message: 'Headshot removed' });
          }}
        />
        <ContactTile kit={kit} />
        <ColorsTile kit={kit} />
        <DisclosuresTile kit={kit} />
        <PhotoTile />
      </div>
    </section>
  );
}

function PhotoTile() {
  const [open, setOpen] = useState(false);
  // Lightweight count fetch — same key the modal uses, so this reuses the
  // cache instead of double-fetching when the modal opens.
  const photosQ = useQuery({
    queryKey: queryKeys.photos({ search: '', tag: null }),
    queryFn: () => listPhotos(),
  });
  const count = photosQ.data?.items.length ?? 0;

  return (
    <>
      <article className="kit-tile">
        <div className="kit-tile__well" aria-hidden="true">
          <Camera size={28} strokeWidth={1.4} />
        </div>
        <h4>Photo library</h4>
        <p>
          {count > 0
            ? `${count} photo${count === 1 ? '' : 's'} ready to drop into your designs.`
            : 'Listing photos you can pull into any design.'}
        </p>
        <button
          type="button"
          className={count > 0 ? 'btn btn--secondary btn--sm' : 'btn btn--primary'}
          onClick={() => setOpen(true)}
        >
          {count > 0 ? 'Open library' : 'Upload your first'}
        </button>
      </article>
      <PhotoLibrary open={open} onClose={() => setOpen(false)} mode="manage" />
    </>
  );
}

// ─── Completion ribbon ────────────────────────────────────────────────────

function CompletionRibbon({ kit }: { kit: BrandKit }) {
  const pct = kit.completion_pct;
  return (
    <div className="kit-progress">
      <div className="kit-progress__bar" aria-hidden="true">
        <div className="kit-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="kit-progress__label">
        {pct}% complete{' '}
        {pct < 100
          ? '· Finish your Brand Kit to auto-fill every design.'
          : '· Nicely done — every new design will inherit this.'}
      </p>
    </div>
  );
}

// ─── Asset tile (logo, headshot) ──────────────────────────────────────────

function AssetTile({
  kind,
  title,
  icon,
  description,
  asset,
  onUpload,
  onRemove,
}: {
  kind: 'logo' | 'headshot';
  title: string;
  icon: React.ReactNode;
  description: string;
  asset: BrandKitAsset | null;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const pushToast = useStudio((s) => s.pushToast);

  async function handleFile(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      pushToast({ type: 'error', message: 'File too large (10 MB max)' });
      return;
    }
    setBusy(true);
    try {
      await onUpload(file);
    } catch (err) {
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Upload failed',
      });
    } finally {
      setBusy(false);
    }
  }

  const inputId = `kit-${kind}-input`;
  return (
    <article className="kit-tile">
      <div className="kit-tile__well kit-tile__well--asset" aria-hidden="true">
        {asset?.url ? (
          <img src={asset.url} alt={`${title} preview`} />
        ) : (
          icon
        )}
      </div>
      <h4>{title}</h4>
      <p>{description}</p>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/svg+xml,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />
      {asset?.url ? (
        <div className="kit-tile__actions">
          <label htmlFor={inputId} className="btn btn--secondary btn--sm">
            <Upload size={14} aria-hidden="true" strokeWidth={1.6} />
            Replace
          </label>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setConfirmRemove(true)}
            disabled={busy}
          >
            <Trash2 size={14} aria-hidden="true" strokeWidth={1.6} />
            Remove
          </button>
        </div>
      ) : (
        <label htmlFor={inputId} className={`btn btn--primary${busy ? ' is-busy' : ''}`}>
          <Upload size={16} aria-hidden="true" strokeWidth={1.6} />
          {busy ? 'Uploading…' : 'Upload'}
        </label>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove your ${title.toLowerCase()}?`}
        body={
          <p>
            New designs won&rsquo;t auto-include it until you upload a new one.
          </p>
        }
        confirmLabel="Remove"
        destructive
        busy={busy}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={async () => {
          setBusy(true);
          try {
            await onRemove();
            setConfirmRemove(false);
          } catch (err) {
            pushToast({
              type: 'error',
              message: err instanceof StudioApiError ? err.message : 'Could not remove',
            });
          } finally {
            setBusy(false);
          }
        }}
      />
    </article>
  );
}

// ─── Contact info tile ────────────────────────────────────────────────────

function ContactTile({ kit }: { kit: BrandKit }) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    agent_name: kit.agent_name ?? '',
    agent_title: kit.agent_title ?? 'REALTOR®',
    agent_phone: kit.agent_phone ?? '',
    agent_email: kit.agent_email ?? '',
    agent_license: kit.agent_license ?? '',
  });

  // Keep form in sync if the underlying kit refreshes (e.g., after a save).
  useEffect(() => {
    setForm({
      agent_name: kit.agent_name ?? '',
      agent_title: kit.agent_title ?? 'REALTOR®',
      agent_phone: kit.agent_phone ?? '',
      agent_email: kit.agent_email ?? '',
      agent_license: kit.agent_license ?? '',
    });
  }, [kit.agent_name, kit.agent_title, kit.agent_phone, kit.agent_email, kit.agent_license]);

  const saveMut = useMutation({
    mutationFn: async () =>
      patchBrandKit({
        agent_name: form.agent_name.trim() || null,
        agent_title: form.agent_title.trim() || null,
        agent_phone: form.agent_phone.trim() || null,
        agent_email: form.agent_email.trim() || null,
        agent_license: form.agent_license.trim() || null,
      }),
    onSuccess: (resp) => {
      qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
      pushToast({ type: 'success', message: 'Contact info saved' });
      setOpen(false);
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Save failed',
      }),
  });

  const filled = [kit.agent_name, kit.agent_phone, kit.agent_email, kit.agent_license].filter(
    Boolean,
  ).length;

  return (
    <article className="kit-tile">
      <div className="kit-tile__well" aria-hidden="true">
        <Phone size={28} strokeWidth={1.4} />
      </div>
      <h4>Contact info</h4>
      <p>Name, title, phone, email, license.</p>

      {open ? (
        <form
          className="kit-tile__form"
          onSubmit={(e) => {
            e.preventDefault();
            saveMut.mutate();
          }}
        >
          <Field label="Name" value={form.agent_name} onChange={(v) => setForm((f) => ({ ...f, agent_name: v }))} />
          <Field label="Title" value={form.agent_title} onChange={(v) => setForm((f) => ({ ...f, agent_title: v }))} />
          <Field label="Phone" value={form.agent_phone} onChange={(v) => setForm((f) => ({ ...f, agent_phone: v }))} />
          <Field label="Email" type="email" value={form.agent_email} onChange={(v) => setForm((f) => ({ ...f, agent_email: v }))} />
          <Field label="License #" value={form.agent_license} onChange={(v) => setForm((f) => ({ ...f, agent_license: v }))} />
          <div className="kit-tile__form-actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          {filled > 0 ? (
            <ul className="kit-tile__summary" role="list">
              {kit.agent_name && <li>{kit.agent_name}</li>}
              {kit.agent_title && <li>{kit.agent_title}</li>}
              {kit.agent_phone && <li>{kit.agent_phone}</li>}
              {kit.agent_email && <li>{kit.agent_email}</li>}
              {kit.agent_license && <li>License {kit.agent_license}</li>}
            </ul>
          ) : null}
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
            {filled > 0 ? 'Edit' : 'Add details'}
          </button>
        </>
      )}
    </article>
  );
}

// ─── Brand colors tile ────────────────────────────────────────────────────

const COLOR_LIMIT = 8;

function ColorsTile({ kit }: { kit: BrandKit }) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [colors, setColors] = useState<string[]>(kit.brand_colors ?? []);
  const [draft, setDraft] = useState('#2c6e35');

  useEffect(() => {
    setColors(kit.brand_colors ?? []);
  }, [kit.brand_colors]);

  const saveMut = useMutation({
    mutationFn: async (next: string[]) => patchBrandKit({ brand_colors: next }),
    onSuccess: (resp) => {
      qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not save colors',
      }),
  });

  const addColor = () => {
    if (!/^#[0-9a-fA-F]{6}$/.test(draft)) {
      pushToast({ type: 'error', message: 'Use a 6-digit hex like #2c6e35' });
      return;
    }
    if (colors.length >= COLOR_LIMIT) {
      pushToast({ type: 'warn', message: `Up to ${COLOR_LIMIT} colors` });
      return;
    }
    if (colors.includes(draft.toLowerCase())) return;
    const next = [...colors, draft.toLowerCase()];
    setColors(next);
    saveMut.mutate(next);
  };

  const removeAt = (idx: number) => {
    const next = colors.filter((_, i) => i !== idx);
    setColors(next);
    saveMut.mutate(next);
  };

  return (
    <article className="kit-tile">
      <div className="kit-tile__well" aria-hidden="true">
        <Palette size={28} strokeWidth={1.4} />
      </div>
      <h4>Brand colors</h4>
      <p>Up to {COLOR_LIMIT} swatches for template variants.</p>

      {colors.length > 0 && (
        <div className="kit-swatches" role="list">
          {colors.map((c, i) => (
            <div key={`${c}-${i}`} className="kit-swatch" role="listitem">
              <span
                className="kit-swatch__chip"
                style={{ backgroundColor: c }}
                title={c}
                aria-label={`Color ${c}`}
              />
              <span className="kit-swatch__hex">{c.toUpperCase()}</span>
              <button
                type="button"
                className="kit-swatch__remove"
                onClick={() => removeAt(i)}
                aria-label={`Remove ${c}`}
              >
                <X size={12} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="kit-color-add">
        <input
          type="color"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Pick a color"
          className="kit-color-add__picker"
        />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="#2c6e35"
          maxLength={7}
          aria-label="Hex color"
          className="kit-color-add__hex"
        />
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={addColor}
          disabled={colors.length >= COLOR_LIMIT || saveMut.isPending}
        >
          <Plus size={14} strokeWidth={2} aria-hidden="true" />
          Add
        </button>
      </div>
    </article>
  );
}

// ─── Disclosures tile ─────────────────────────────────────────────────────

function DisclosuresTile({ kit }: { kit: BrandKit }) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    brokerage_name: kit.brokerage_name ?? '',
    brokerage_address: kit.brokerage_address ?? '',
    brokerage_disclosures: kit.brokerage_disclosures ?? '',
  });

  useEffect(() => {
    setForm({
      brokerage_name: kit.brokerage_name ?? '',
      brokerage_address: kit.brokerage_address ?? '',
      brokerage_disclosures: kit.brokerage_disclosures ?? '',
    });
  }, [kit.brokerage_name, kit.brokerage_address, kit.brokerage_disclosures]);

  const saveMut = useMutation({
    mutationFn: async () =>
      patchBrandKit({
        brokerage_name: form.brokerage_name.trim() || null,
        brokerage_address: form.brokerage_address.trim() || null,
        brokerage_disclosures: form.brokerage_disclosures.trim() || null,
      }),
    onSuccess: (resp) => {
      qc.setQueryData(qk.brandKit, { brand_kit: resp.brand_kit });
      pushToast({ type: 'success', message: 'Disclosures saved' });
      setOpen(false);
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Save failed',
      }),
  });

  const filled = [kit.brokerage_name, kit.brokerage_address, kit.brokerage_disclosures].filter(
    Boolean,
  ).length;

  return (
    <article className="kit-tile">
      <div className="kit-tile__well" aria-hidden="true">
        <Scale size={28} strokeWidth={1.4} />
      </div>
      <h4>Brokerage disclosures</h4>
      <p>Equal Housing, license, brokerage block.</p>

      {open ? (
        <form
          className="kit-tile__form"
          onSubmit={(e) => {
            e.preventDefault();
            saveMut.mutate();
          }}
        >
          <Field
            label="Brokerage name"
            value={form.brokerage_name}
            onChange={(v) => setForm((f) => ({ ...f, brokerage_name: v }))}
          />
          <Field
            label="Brokerage address"
            value={form.brokerage_address}
            onChange={(v) => setForm((f) => ({ ...f, brokerage_address: v }))}
          />
          <Field
            label="Disclosures"
            value={form.brokerage_disclosures}
            onChange={(v) => setForm((f) => ({ ...f, brokerage_disclosures: v }))}
            multiline
          />
          <div className="kit-tile__form-actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          {filled > 0 ? (
            <ul className="kit-tile__summary" role="list">
              {kit.brokerage_name && <li>{kit.brokerage_name}</li>}
              {kit.brokerage_address && <li>{kit.brokerage_address}</li>}
              {kit.brokerage_disclosures && (
                <li className="kit-tile__summary-clip">
                  {kit.brokerage_disclosures.slice(0, 80)}
                  {kit.brokerage_disclosures.length > 80 ? '…' : ''}
                </li>
              )}
            </ul>
          ) : null}
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
            {filled > 0 ? 'Edit' : 'Add disclosures'}
          </button>
        </>
      )}
    </article>
  );
}

// ─── Reusable field ───────────────────────────────────────────────────────

function Field({
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  multiline?: boolean;
}) {
  return (
    <label className="kit-field">
      <span className="kit-field__label">{label}</span>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

// keep linter happy with currently-unused icons we'll bring in next pass
void Check;
